import * as React from 'react';
import { Button, Toggle } from '@teamsnap/teamsnap-ui';
import { Icon } from '@teamsnap/snap-ui';

// Local Imports
import { RegistrationFormAdjustment } from './AddOnFees/models';
import { currencyGivenPennies } from 'core/utils';
import { DateService } from 'frontend-toolkit';

type Props = {
  index: number;
  discount: RegistrationFormAdjustment;
  onEdit: (discount: RegistrationFormAdjustment) => void;
  onToggle: (discount: RegistrationFormAdjustment) => void;
  isLoading?: boolean;
  determineDateFromTimezone: (date: string) => Date;
};

const getDiscountType = (appliesTo: string | undefined) => {
  if (appliesTo === 'registration_fee') {
    return 'off registration option fee';
  } else if (appliesTo === 'fees') {
    return 'off add on fees';
  } else {
    return 'off everything';
  }
};

const DiscountItem: React.FC<Props> = ({ index, discount, onEdit, onToggle, isLoading, determineDateFromTimezone }) => {
  const startDate = discount?.startDate ? determineDateFromTimezone(discount.startDate) : null;
  const endDate = discount?.endDate
    ? DateService.removeDaysFromDate(1, determineDateFromTimezone(discount.endDate))
    : null;

  let dateString = '';

  if (startDate && endDate) {
    if (startDate.getFullYear() === endDate.getFullYear()) {
      dateString = `${(startDate.getMonth() + 1).toString().padStart(2, '0')}/${startDate
        .getDate()
        .toString()
        .padStart(2, '0')}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate
        .getDate()
        .toString()
        .padStart(2, '0')}, ${startDate.getFullYear()}`;
    } else {
      dateString = `${(startDate.getMonth() + 1).toString().padStart(2, '0')}/${startDate
        .getDate()
        .toString()
        .padStart(2, '0')}, ${startDate.getFullYear()} - ${(endDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${endDate.getDate().toString().padStart(2, '0')}, ${endDate.getFullYear()}`;
    }
  }

  let qualifyingCartCountString = '';
  if (discount?.qualifyingCount && discount?.qualifyingOperator) {
    qualifyingCartCountString = `when ${discount.qualifyingOperator !== 'equal_to' ? 'more than' : ''} ${
      discount.qualifyingCount
    } registration options are in cart`;
  }

  return (
    <article
      key={`discount-${index}-${discount.id}}`}
      data-testid={`discounts-item-${index}-${discount.id}`}
      className="sui-border sui-border-gray-80 sui-border-solid sui-p-2 sui-rounded sui-my-1"
    >
      <div className="sui-flex sui-items-center sui-justify-between">
        <div className="sui-flex sui-items-center">
          <Toggle
            name={`discounts-item-${index}-${discount.id}-toggle`}
            data-testid={`discounts-item-${index}-${discount.id}-toggle`}
            inputProps={{
              checked: discount.enabled,
              disabled: isLoading,
              onChange: () => {
                onToggle(discount);
              },
            }}
          />
          <span
            data-testid={`discounts-item-${index}-${discount.id}-name`}
            className="sui-text-gray-10 sui-heading-sm sui-pl-1"
          >
            {discount.formAdjustmentDefinition.name}
          </span>
        </div>
        <Button
          isDisabled={isLoading}
          type="link"
          mods="sui-leading-none sui-text-gray-30"
          onClick={() => onEdit(discount)}
          testId={`discounts-item-${index}-${discount.id}-edit`}
        >
          <Icon name="edit" />
        </Button>
      </div>
      <div className="sui-body sui-flex sui-mt-5 sui-my-1">
        <strong className="sui-text-gray-10 sui-pr-1">Discount amount: </strong>{' '}
        <span data-testid={`discounts-item-${index}-${discount.id}-amount`} className="sui-text-gray-30">
          {discount?.percentage !== null ? `${discount.percentage / 100}%` : `${currencyGivenPennies(discount.amount)}`}{' '}
          {getDiscountType(discount?.appliesTo)}
        </span>
      </div>
      <div className="sui-body sui-flex">
        <strong className="sui-text-gray-10 sui-pr-1">Code: </strong>{' '}
        <span data-testid={`discounts-item-${index}-${discount.id}-code`} className="sui-text-gray-30">
          {discount?.couponCode ?? 'No code, Automatic'}
        </span>
      </div>
      {(dateString || qualifyingCartCountString) && (
        <div className="sui-body sui-flex sui-mt-1">
          <strong className="sui-text-gray-10 sui-pr-1">Valid: </strong>{' '}
          <span data-testid={`discounts-item-${index}-${discount.id}-valid`} className="sui-text-gray-30">
            {dateString}
            {dateString && qualifyingCartCountString && '; '}
            {qualifyingCartCountString}
          </span>
        </div>
      )}
    </article>
  );
};

DiscountItem.defaultProps = {
  isLoading: false,
};

export default DiscountItem;
